import { useState } from "react";
import type { InventionElementResponse } from "@patent-studio/api-types";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { Note } from "@/components/ui/Note";
import { refusalDetail } from "@/services/api-client";
import { ElementLadder, type LadderWrites } from "./ElementLadder";
import type { ElementCardProps, Openable } from "./ElementCard";

/**
 * The question asked before an element goes, held open until the store answers:
 * closed once it has gone, kept with the reason when the removal is refused, so
 * the attorney reads why and can try again. The reason is said inside the
 * dialog because the page behind it is hidden while it is open.
 */
function RemoveQuestion({
  open,
  text,
  only,
  busy,
  onRemove,
  onClose,
}: {
  open: boolean;
  text: string;
  /** Whether this is the last element, so removing it empties the invention. */
  only: boolean;
  busy: boolean;
  onRemove: () => Promise<unknown>;
  onClose: () => void;
}) {
  const [refused, setRefused] = useState<string | null>(null);
  return (
    <ConfirmDialog
      open={open}
      title="Remove this element?"
      description={`"${text}" comes off the list and off every position that rests on it. This cannot be undone.`}
      warn={only}
      note={
        only
          ? "This is the only element. Removing it returns the page to its empty state."
          : undefined
      }
      actionLabel="Remove element"
      actionDisabled={busy}
      destructive
      holdsOpen
      onCancel={() => {
        setRefused(null);
        onClose();
      }}
      onConfirm={() => {
        setRefused(null);
        onRemove()
          .then(onClose)
          .catch((error: unknown) => {
            setRefused(
              refusalDetail(
                error,
                "Could not remove this element. Please try again.",
              ),
            );
          });
      }}
    >
      {refused !== null && (
        <Note tone="destructive" role="alert">
          {refused}
        </Note>
      )}
    </ConfirmDialog>
  );
}

/**
 * What is open on an element.
 *
 * Only one awaiting attention is offered confirmation: an element grounded in
 * the disclosure is never presented for it.
 */
function ElementActions({
  awaiting,
  busy,
  amendLocked,
  onAddFallback,
  onConfirm,
  onAmend,
  onRemove,
}: {
  awaiting: boolean;
  busy: boolean;
  /** For an element with no fallbacks: the way to its first, at the row's start. */
  onAddFallback?: () => void;
  /** Another element's editor is open; only one may be at a time. */
  amendLocked: boolean;
  onConfirm: () => void;
  onAmend: () => void;
  onRemove: () => void;
}) {
  return (
    // At the right, as the design sets a card's acts, with the act that
    // settles the element last.
    <div className="mt-2.5 flex justify-end gap-[7px] border-t border-dashed border-border pt-[9px]">
      {onAddFallback !== undefined && (
        <Button
          type="button"
          variant="quiet"
          size="dense"
          className="mr-auto"
          onClick={onAddFallback}
        >
          Add fallback
        </Button>
      )}
      <Button
        type="button"
        variant="danger-outline"
        size="dense"
        onClick={onRemove}
      >
        Remove
      </Button>
      <Button
        type="button"
        variant="secondary"
        size="dense"
        onClick={onAmend}
        disabled={amendLocked}
      >
        Amend
      </Button>
      {awaiting && (
        <Button
          type="button"
          variant="confirm"
          size="dense"
          onClick={onConfirm}
          disabled={busy}
        >
          Confirm
        </Button>
      )}
    </div>
  );
}

/** Under the provenance: why the element was flagged, if it was, and the ladder beneath it. */
function Beneath({
  element,
  busy,
  ladder,
  openable,
  onOpenSource,
  addingFirst,
  onAddingFirstDone,
}: {
  element: InventionElementResponse;
  busy: boolean;
  ladder: LadderWrites;
  openable: Openable;
  onOpenSource: (fileId: string) => void;
  addingFirst: boolean;
  onAddingFirstDone: () => void;
}) {
  return (
    <>
      {element.flag_reason !== null && (
        <p className="mt-1.5 text-[11px] leading-[1.45] text-muted-foreground">
          {element.flag_reason}
        </p>
      )}
      <ElementLadder
        element={element}
        busy={busy}
        writes={ladder}
        openable={openable}
        onOpenSource={onOpenSource}
        addingFirst={addingFirst}
        onAddingFirstDone={onAddingFirstDone}
      />
    </>
  );
}

/**
 * The foot of an element's card: the ladder, the question before a removal,
 * and the acts. Which editor is open is the panel's to know, since only one
 * may be; the removal question and the first fallback's form are the card's.
 */
export function CardFoot({
  awaiting,
  ...props
}: ElementCardProps & { awaiting: boolean }) {
  const { element, busy, amending, amendLocked, only, ladder } = props;
  const [removing, setRemoving] = useState(false);
  const [addingFirst, setAddingFirst] = useState(false);
  const bare = element.fallbacks.length === 0;
  return (
    <>
      <Beneath
        element={element}
        busy={busy}
        ladder={ladder}
        openable={props.openable}
        onOpenSource={props.onOpenSource}
        addingFirst={addingFirst && bare}
        onAddingFirstDone={() => {
          setAddingFirst(false);
        }}
      />
      <RemoveQuestion
        open={removing}
        text={element.text}
        only={only}
        busy={busy}
        onRemove={props.onRemove}
        onClose={() => {
          setRemoving(false);
        }}
      />
      {!amending && (
        <ElementActions
          awaiting={awaiting}
          busy={busy}
          amendLocked={amendLocked}
          onAddFallback={
            bare && !addingFirst
              ? () => {
                  setAddingFirst(true);
                }
              : undefined
          }
          onConfirm={props.onConfirm}
          onAmend={props.onAmendOpen}
          onRemove={() => {
            setRemoving(true);
          }}
        />
      )}
    </>
  );
}
